/**
 * Cargar el SDK de Google Maps una sola vez, y sólo si hace falta.
 *
 * ## Por qué a mano y no con `@googlemaps/js-api-loader`
 *
 * Son treinta líneas y una dependencia menos en un paquete que se abre desde el
 * móvil de un promotor dentro de un recinto. Lo único delicado —que dos campos
 * de dirección en la misma pantalla no inyecten el script dos veces— se resuelve
 * guardando la promesa.
 *
 * ## Perezoso a propósito
 *
 * El script son ~200 kB y una petición a un tercero. Cargarlo al arrancar la
 * aplicación lo pagaría **todo el mundo**, incluida la mayoría que nunca abre un
 * formulario con dirección. Se pide la primera vez que un campo de dirección
 * entra en pantalla.
 *
 * ## Sin clave configurada no se carga nada
 *
 * `VITE_GOOGLE_MAPS_API_KEY` vacía no es un error: el campo de dirección sigue
 * funcionando como texto libre, que es exactamente lo que había antes. Lo que
 * no puede pasar es que una pantalla se rompa —o peor, quede a medias— por una
 * variable de entorno que nadie puso.
 */

export const GOOGLE_MAPS_KEY: string = import.meta.env['VITE_GOOGLE_MAPS_API_KEY'] ?? '';

/** ¿Se puede ofrecer autocompletado y mapa? */
export const mapsDisponible = (): boolean => GOOGLE_MAPS_KEY.trim().length > 0;

let promesa: Promise<void> | null = null;

/**
 * Carga el SDK con las bibliotecas de lugares y marcadores.
 *
 * Rechaza si no hay clave o si el script no llega: quien llama decide qué
 * enseñar, y en este componente es «el mapa no está disponible», no una
 * pantalla en blanco.
 */
export function cargarGoogleMaps(): Promise<void> {
  if (promesa) return promesa;

  if (!mapsDisponible()) {
    promesa = Promise.reject(new Error('Sin VITE_GOOGLE_MAPS_API_KEY'));
    // Sin esto, una promesa rechazada que nadie mira todavía sale por consola
    // como «unhandled rejection» en cada arranque.
    promesa.catch(() => undefined);
    return promesa;
  }

  promesa = (async () => {
    /*
      No se espera a `script.onload`: se espera a que la API EXISTA.

      `onload` parecía lo natural y no aguanta la realidad. Con `loading=async`
      sólo avisa de que llegó el arranque, no las bibliotecas; con el script ya
      en la caché del navegador el evento puede haber pasado antes de que se
      enganche el manejador; y en desarrollo, la recarga en caliente crea una
      segunda instancia de este módulo que vuelve a inyectar el mismo `src`. En
      esos casos la promesa se quedaba pendiente para siempre y el campo se
      quedaba mudo sin un solo error en consola — que es justo lo que costó
      encontrar.

      Sondear `google.maps.importLibrary` no tiene ninguno de esos problemas:
      da igual quién cargara el script, cuántas veces, ni cuándo.
    */
    if (!document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`)) {
      const script = document.createElement('script');
      const params = new URLSearchParams({
        key: GOOGLE_MAPS_KEY,
        libraries: 'places,marker',
        // `loading=async` es lo que Google pide desde 2024; sin él avisa por
        // consola en cada carga.
        loading: 'async',
        v: 'weekly',
      });
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      document.head.appendChild(script);
    }

    // 15 s: en una red de recinto el SDK tarda, y rendirse antes deja el campo
    // sin sugerencias justo donde más falta hacen.
    const listo = await esperarA(() => typeof window.google?.maps?.importLibrary === 'function', 15_000);
    if (!listo) {
      throw new Error('Google Maps no respondió — revisa la clave y sus dominios permitidos');
    }

    // Y ahora sí, las bibliotecas. `importLibrary` es idempotente.
    await Promise.all([
      google.maps.importLibrary('places'),
      google.maps.importLibrary('maps'),
    ]);
  })();

  // Un rechazo que nadie mire todavía saldría por consola como «unhandled».
  promesa.catch(() => undefined);
  return promesa;
}

/** Lo que devuelve el campo: el texto Y dónde está, si se eligió en el mapa. */
export interface Lugar {
  address: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string | null;
}

/** Un punto de partida razonable cuando no hay nada elegido: Ciudad de México. */
export const CENTRO_POR_DEFECTO = { lat: 19.4326, lng: -99.1332 };

/**
 * Espera a que una condición se cumpla, sondeando.
 *
 * Cada 50 ms: lo bastante fino para que no se note y lo bastante grueso para
 * no gastar batería en el móvil de quien está en un stand.
 */
function esperarA(condicion: () => boolean, msMaximo: number): Promise<boolean> {
  return new Promise((resolve) => {
    if (condicion()) return resolve(true);
    const desde = Date.now();
    const id = setInterval(() => {
      if (condicion()) {
        clearInterval(id);
        resolve(true);
      } else if (Date.now() - desde > msMaximo) {
        clearInterval(id);
        resolve(false);
      }
    }, 50);
  });
}
